import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiBell, FiChevronDown, FiUser, FiMenu, FiX } from "react-icons/fi";
import { authStorageKeys, notificationHref as portalNotificationHref, isScanLimitNotification, isBillingNotification, isAccountReadyNotification } from "./portalHelpers";
import { PORTAL_HEADER } from "./storage";
import { fetchAdminWithRetry } from "./fetchAdminWithRetry";
import { confirmPortalLogout } from "../../shared/portalLogoutConfirm";

const formatNotificationTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const checkinIdFromNotification = (item) => {
  const meta = item?.metadata || {};
  if (meta?.checkin_id != null && meta.checkin_id !== "") return String(meta.checkin_id);
  const key = String(item?.category_key || "");
  if (key.startsWith("checkin:")) return key.split(":")[1] || "";
  return "";
};

export function createPortalHeader({
  portalId,
  portalBasePath,
  editProfilePath,
  extraProfileLinks = [],
  useUser,
  API_BASE,
  clearPortalStorage,
  loginPathForRole,
  resolveMediaUrl,
  resolveStoredOrgLogo,
  logoFallback,
  applyThemeToDocument,
  getStoredTheme,
  THEME_STORAGE_KEY,
  fetchCheckinQueue,
  renderToolbar,
}) {
  const storageKeys = authStorageKeys(portalId);
  const DISMISSED_CHECKIN_TOAST_KEY = storageKeys.dismissedToasts;
  const CHECKIN_TOAST_MAX_AGE_MS = 2 * 60 * 60 * 1000;
  const MAX_STORED_DISMISSED_TOASTS = 200;

  const readDismissedToastIds = () => {
    try {
      const raw = localStorage.getItem(DISMISSED_CHECKIN_TOAST_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      return new Set();
    }
  };

  const persistDismissedToastIds = (ids) => {
    try {
      const list = [...ids].slice(-MAX_STORED_DISMISSED_TOASTS);
      localStorage.setItem(DISMISSED_CHECKIN_TOAST_KEY, JSON.stringify(list));
    } catch {
      // Ignore storage failures.
    }
  };

  const notificationIsRecentEnough = (item, maxAgeMs) => {
    const ts = new Date(item?.created_at || 0).getTime();
    if (!ts || Number.isNaN(ts)) return false;
    return Date.now() - ts <= maxAgeMs;
  };

  const isCheckinNotification = (item) => {
    const meta = item?.metadata || {};
    const title = String(item?.title || "").toLowerCase();
    return (
      meta?.type === "checkin" ||
      meta?.type === "service_started" ||
      String(item?.category_key || "").startsWith("checkin:") ||
      title.includes("check-in") ||
      title.includes("service started")
    );
  };

  const isOrderNotification = (item) => {
    const meta = item?.metadata || {};
    const key = String(item?.category_key || "");
    const title = String(item?.title || "").toLowerCase();
    return (
      meta?.type === "order" ||
      key.startsWith("salon-order:") ||
      title.includes("order placed")
    );
  };

  const isLocationNotification = (item) => {
    const meta = item?.metadata || {};
    const key = String(item?.category_key || "");
    const title = String(item?.title || "").toLowerCase();
    return (
      meta?.type === "location_request" ||
      meta?.type === "location_request_decision" ||
      key.startsWith("loc-req") ||
      title.includes("location change") ||
      title.includes("location approved") ||
      title.includes("location rejected")
    );
  };

  const notificationHref = (item) => portalNotificationHref(item, portalBasePath);

  const Header = ({ showLogo = false, onToggleSidebar, sidebarOpen = false }) => {
  const { user, loading } = useUser();
  const [open, setOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [checkinToast, setCheckinToast] = useState(null);
  const [theme, setTheme] = useState(() => getStoredTheme());
  const navigate = useNavigate();
  const location = useLocation();
  const notificationRef = useRef(null);
  const dismissedToastIdsRef = useRef(readDismissedToastIds());
  const checkinToastRef = useRef(null);

  const today = new Date().toLocaleDateString("en-GB");

  useEffect(() => {
    checkinToastRef.current = checkinToast;
  }, [checkinToast]);

  const handleLogout = async () => {
    const ok = await confirmPortalLogout({
      title: "Sign out?",
      description: "You will need to sign in again to continue.",
      confirmLabel: "Sign out",
    });
    if (!ok) return;
    clearPortalStorage();
    navigate(loginPathForRole());
  };

  const dismissCheckinToast = useCallback(async (toast, { markRead = true } = {}) => {
    if (!toast?.id) {
      setCheckinToast(null);
      return;
    }
    const idKey = String(toast.id);
    dismissedToastIdsRef.current.add(idKey);
    persistDismissedToastIds(dismissedToastIdsRef.current);
    setCheckinToast((current) => (current?.id === toast.id ? null : current));
    if (markRead) {
      const token = localStorage.getItem(storageKeys.token);
      if (!token && !localStorage.getItem(storageKeys.userId)) return;
      try {
        await fetch(`${API_BASE}/notifications/read`, {
          method: "POST",
          headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ notification_id: toast.id }),
        });
        setNotifications((prev) =>
          prev.map((item) => (item.id === toast.id ? { ...item, is_read: true } : item)),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        // Keep toast dismissed even if read update fails.
      }
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(storageKeys.userId)) return;

    let timer;
    let cancelled = false;

    const notificationHeaders = {
      [PORTAL_HEADER]: portalId,
    };

    const fetchNotifications = async ({ silent = false } = {}) => {
      if (!silent) {
        setNotificationsLoading(true);
      }
      try {
        const [notificationsRes, queue] = await Promise.all([
          fetchAdminWithRetry(`${API_BASE}/notifications`, {
            headers: notificationHeaders,
            credentials: "include",
            timeoutMs: 25000,
          }),
          fetchCheckinQueue({ status: "all" }).catch(() => ({ items: [] })),
        ]);
        if (cancelled) return;

        const data = await notificationsRes.json();
        const list = data?.notifications || [];
        setNotifications(list);
        setUnreadCount(Number(data?.unread_count || 0));

        const statusById = new Map(
          (queue?.items || []).map((item) => [
            String(item.id),
            String(item?.status || "").toLowerCase(),
          ]),
        );

        const currentToast = checkinToastRef.current;
        const checkinNotes = list
          .filter((item) => {
            if (item?.is_read) return false;
            if (dismissedToastIdsRef.current.has(String(item.id))) return false;
            if (isOrderNotification(item)) return true;
            if (isLocationNotification(item)) return true;
            if (isBillingNotification(item)) return true;
            if (isAccountReadyNotification(item)) return true;
            if (!isCheckinNotification(item)) return false;
            if (!notificationIsRecentEnough(item, CHECKIN_TOAST_MAX_AGE_MS)) return false;
            const checkinId = checkinIdFromNotification(item);
            if (!checkinId) return false;
            const st = statusById.get(checkinId);
            const started =
              (item?.metadata || {}).type === "service_started" ||
              String(item?.title || "").toLowerCase().includes("service started");
            if (started) return st === "in_service" || st === "in-service" || !st;
            return st === "active";
          })
          .sort((a, b) => {
            const ta = new Date(a.created_at || 0).getTime();
            const tb = new Date(b.created_at || 0).getTime();
            return tb - ta;
          });

        if (currentToast) {
          const currentCheckinId = String(currentToast.checkinId || "");
          const stillUnread = list.some(
            (item) => item.id === currentToast.id && !item.is_read,
          );
          const toastKind = currentToast.kind || "checkin";
          const st = currentCheckinId ? statusById.get(currentCheckinId) : "";
          const stillActive =
            toastKind === "billing" ||
            toastKind === "account" ||
            toastKind === "order" ||
            toastKind === "location" ||
            toastKind === "scan-limit" ||
            (toastKind === "started" && (st === "in_service" || st === "in-service" || !st)) ||
            (toastKind !== "started" &&
              toastKind !== "order" &&
              toastKind !== "location" &&
              toastKind !== "scan-limit" &&
              toastKind !== "billing" &&
              toastKind !== "account" &&
              st === "active");
          const stillEligible =
            stillUnread &&
            stillActive &&
            !dismissedToastIdsRef.current.has(String(currentToast.id));

          if (!stillEligible) {
            setCheckinToast(null);
            if (currentToast.id != null) {
              dismissedToastIdsRef.current.add(String(currentToast.id));
              persistDismissedToastIds(dismissedToastIdsRef.current);
            }
          } else {
            const newest = checkinNotes[0];
            // Keep current toast unless a newer active check-in arrives.
            if (!newest || newest.id === currentToast.id) {
              return;
            }
            const currentTs = new Date(currentToast.createdAt || 0).getTime();
            const newestTs = new Date(newest.created_at || 0).getTime();
            if (newestTs <= currentTs) {
              return;
            }
          }
        }

        if (checkinNotes.length) {
          const newest = checkinNotes[0];
          const started =
            (newest?.metadata || {}).type === "service_started" ||
            String(newest?.title || "").toLowerCase().includes("service started");
          const kind = isBillingNotification(newest)
            ? "billing"
            : isAccountReadyNotification(newest)
              ? "account"
              : isLocationNotification(newest)
            ? "location"
            : isOrderNotification(newest)
              ? "order"
              : started
                ? "started"
                : "checkin";
          setCheckinToast({
            id: newest.id,
            checkinId: checkinIdFromNotification(newest),
            kind,
            href: portalNotificationHref(newest, portalBasePath),
            title:
              newest.title ||
              (kind === "billing"
                ? "Payment received"
                : kind === "account"
                  ? "Account ready"
                  : kind === "location"
                    ? "Salon location change request"
                    : kind === "order"
                      ? "Order placed"
                      : kind === "started"
                        ? "Service started"
                        : "New check-in"),
            message: newest.message || "",
            code: newest.metadata?.customer_code || "",
            createdAt: newest.created_at || null,
          });
          window.dispatchEvent(new CustomEvent("checkin-queue-updated"));
        } else {
          const scanNotes = list
            .filter((item) => {
              if (item?.is_read) return false;
              if (dismissedToastIdsRef.current.has(String(item.id))) return false;
              return isScanLimitNotification(item);
            })
            .sort((a, b) => {
              const ta = new Date(a.created_at || 0).getTime();
              const tb = new Date(b.created_at || 0).getTime();
              return tb - ta;
            });

          if (scanNotes.length) {
            const newest = scanNotes[0];
            setCheckinToast({
              id: newest.id,
              checkinId: "",
              kind: "scan-limit",
              href: notificationHref(newest),
              title: newest.title || "Scan limit",
              message: newest.message || "",
              code: "",
              createdAt: newest.created_at || null,
            });
          }
        }
      } catch {
        if (!cancelled) {
          setNotifications([]);
          setUnreadCount(0);
        }
      } finally {
        if (!silent && !cancelled) {
          setNotificationsLoading(false);
        }
      }
    };

    fetchNotifications({ silent: notificationsOpen });

    if (!notificationsOpen) {
      timer = setInterval(() => {
        fetchNotifications({ silent: true });
      }, 10000);
    }

    const onQueueUpdated = () => {
      fetchNotifications({ silent: true });
    };
    window.addEventListener("checkin-queue-updated", onQueueUpdated);

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("checkin-queue-updated", onQueueUpdated);
    };
  }, [location.pathname, notificationsOpen]);

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  useEffect(() => {
    const syncTheme = () => {
      const nextTheme = getStoredTheme();
      setTheme((currentTheme) => (currentTheme === nextTheme ? currentTheme : nextTheme));
      applyThemeToDocument(nextTheme);
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("themechange", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("themechange", syncTheme);
    };
  }, []);

  // Close notification when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    if (notificationsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [notificationsOpen]);

  const markNotificationRead = async (notificationId) => {
    const token = localStorage.getItem(storageKeys.token);
    if (!token && !localStorage.getItem(storageKeys.userId)) return;

    try {
      await fetch(`${API_BASE}/notifications/read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(notificationId ? { notification_id: notificationId } : {}),
      });

      setNotifications((prev) => prev.map((item) => (
        !notificationId || item.id === notificationId
          ? { ...item, is_read: true }
          : item
      )));
      if (!notificationId) {
        setUnreadCount(0);
      } else {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch {
      // Keep existing UI state if read update fails.
    }
  };

  const notificationTone = (type) => {
    if (type === "warning") return "salon-notifications-item--warning";
    if (type === "success") return "salon-notifications-item--success";
    return "";
  };

  if (loading) {
    return <div className="p-4 text-white">Loading...</div>;
  }

  return (
    <div className="header-shell relative z-50 w-full pt-2 md:pt-2.5 lg:pt-3">
      {checkinToast ? (
        <div className={`checkin-toast mb-3 flex items-start justify-between gap-3 rounded-xl border px-4 py-3 shadow-lg backdrop-blur-md${checkinToast.kind === "location" || checkinToast.kind === "scan-limit" ? " checkin-toast--warning" : ""}`}>
          <button
            type="button"
            className="min-w-0 flex-1 text-left"
            onClick={() => {
              dismissCheckinToast(checkinToast);
              navigate(checkinToast.href || `${portalBasePath}/check-ins`);
            }}
          >
            <p className="checkin-toast__title text-sm font-semibold">{checkinToast.title}</p>
            <p className="checkin-toast__message mt-0.5 text-sm">
              {checkinToast.message}
              {checkinToast.code ? (
                <span className="checkin-toast__code ml-2 font-mono tracking-widest">{checkinToast.code}</span>
              ) : null}
            </p>
          </button>
          <button
            type="button"
            aria-label="Dismiss check-in notification"
            className="checkin-toast__close rounded-lg px-2 py-1 text-lg leading-none"
            onClick={() => dismissCheckinToast(checkinToast)}
          >
            ×
          </button>
        </div>
      ) : null}
      <div className="header-shell__inner header-shell__inner--integrated flex min-h-[3.25rem] w-full flex-wrap items-center justify-end gap-2 px-1 py-1 md:min-h-[3.5rem] md:gap-3 lg:h-14 lg:flex-nowrap lg:px-2">
        <div className="header-shell__left flex min-w-0 flex-1 items-center gap-3 lg:hidden">
          {typeof onToggleSidebar === "function" ? (
            <button
              type="button"
              className="header-menu-btn"
              aria-label={sidebarOpen ? "Close menu" : "Open menu"}
              aria-expanded={sidebarOpen}
              onClick={onToggleSidebar}
            >
              {sidebarOpen ? <FiX size={18} /> : <FiMenu size={18} />}
            </button>
          ) : null}
          {showLogo ? (
            <div className="pl-1 lg:pl-4">
              <img src={logoFallback} alt="Nyafaz Logo" className="h-8 object-contain md:h-9 lg:h-10" />
            </div>
          ) : null}
        </div>

        {typeof renderToolbar === "function" ? (
          <div className="header-shell__toolbar order-3 w-full min-w-0 lg:order-none lg:flex lg:flex-1 lg:justify-center lg:px-3">
            {renderToolbar()}
          </div>
        ) : null}

          <div className="header-shell__actions flex w-full min-w-0 flex-wrap items-center justify-end gap-2 sm:gap-2.5 md:w-auto md:flex-nowrap lg:ml-auto">
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 md:gap-3 lg:gap-4">
            {/* <div className="relative w-8 h-8 rounded-full bg-white/5 backdrop-blur-3xl shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] before:content-[''] before:absolute before:inset-0 before:rounded-full before:pointer-events-none before:border-[1px] before:border-white/50 before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] flex items-center justify-center cursor-pointer hover:bg-white/10 transition">
              <img src={setimg} alt="Settings" className="w-5 h-5 brightness-0 opacity-80" />
            </div> */}
           {/* <div className="relative w-8 h-8 rounded-full bg-white/5 backdrop-blur-3xl shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] before:content-[''] before:absolute before:inset-0 before:rounded-full before:pointer-events-none before:border-[1px] before:border-white/50 before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] flex items-center justify-center cursor-pointer hover:bg-white/10 transition">
              <img src={mailimg} alt="Mail" className="w-5 h-5 brightness-0 opacity-80" />
            </div> */}
            <div className="relative" ref={notificationRef}>
              <button
                type="button"
                onClick={() => {
                  setNotificationsOpen((prev) => !prev);
                  setOpen(false);
                }}
                className="header-shell__icon-btn relative flex h-9 w-9 items-center justify-center rounded-full"
                title="Notifications"
              >
                <FiBell className="header-icon w-5 h-5" aria-hidden />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 border border-white/60 text-[10px] text-white font-bold flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <div className="salon-notifications-panel">
                  <div className="salon-notifications-panel__header">
                    <div>
                      <p className="salon-notifications-panel__title">Notifications</p>
                      <p className="salon-notifications-panel__subtitle">{unreadCount} unread</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => markNotificationRead(null)}
                      className="salon-notifications-panel__mark-read disabled:opacity-50"
                      disabled={!unreadCount}
                    >
                      Mark all read
                    </button>
                  </div>

                  <div className="salon-notifications-panel__list">
                    {notificationsLoading ? (
                      <div className="salon-notifications-panel__loading px-3 py-6 text-center">Loading notifications...</div>
                    ) : notifications.length ? (
                      notifications.map((item) => (
                        <div
                          key={item.id}
                          className={`salon-notifications-item ${notificationTone(item.type)} ${item.is_read ? "is-read" : ""}`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              if (!item.is_read) markNotificationRead(item.id);
                              const href = notificationHref(item);
                              if (href) {
                                setNotificationsOpen(false);
                                navigate(href);
                              }
                            }}
                            className="w-full text-left"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="salon-notifications-item__title">{item.title}</p>
                                <p className="salon-notifications-item__message">{item.message}</p>
                              </div>
                              {!item.is_read && <span className="mt-1 w-2 h-2 rounded-full bg-red-500 shrink-0" />}
                            </div>
                            <p className="salon-notifications-item__time">{formatNotificationTime(item.created_at)}</p>
                          </button>
                          
                        </div>
                      ))
                    ) : (
                      <div className="salon-notifications-panel__empty px-3 py-6 text-center">No notifications yet.</div>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>

          <div
            className="header-shell__profile-pill relative flex min-w-0 cursor-pointer items-center gap-2 rounded-full py-1 pl-1 pr-2.5 md:gap-2.5 md:pr-3"
            onClick={() => {
              setOpen(!open);
              setNotificationsOpen(false);
            }}
          >
            <div className="relative group">
              {(() => {
                const logoSrc =
                  resolveMediaUrl(user?.org_logo || user?.data?.org_logo || user?.user?.org_logo) ||
                  resolveStoredOrgLogo();
                return logoSrc ? (
                  <div className="header-shell__avatar h-9 w-9 overflow-hidden rounded-full p-0.5 md:h-10 md:w-10">
                    <img
                      src={logoSrc}
                      alt="Organization"
                      className="h-full w-full rounded-full object-contain"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = logoFallback;
                      }}
                    />
                  </div>
                ) : (
                  <div className="header-shell__avatar flex h-9 w-9 items-center justify-center rounded-full md:h-10 md:w-10">
                    <FiUser className="header-icon" size={20} />
                  </div>
                );
              })()}
            </div>
            <div className="header-shell__user-meta hidden min-w-0 text-left sm:block">
              <p className="max-w-[9rem] truncate text-sm font-semibold text-[color:var(--portal-ink)] md:max-w-[12rem]">
                {user?.fullName || user?.data?.fullName || user?.user?.fullName || user?.full_name || "Admin"}
              </p>
            </div>
            <FiChevronDown className="header-shell__profile-chevron hidden h-4 w-4 shrink-0 sm:block" aria-hidden />

            {/* DROPDOWN */}
            {open && (
              <div className="header-shell__dropdown absolute right-0 top-[calc(100%+0.5rem)] z-[9999] w-48 overflow-hidden rounded-2xl"
              >
                {editProfilePath ? (
                  <button
                    type="button"
                    onClick={() => navigate(editProfilePath)}
                    className="w-full text-left px-4 py-2 hover:bg-white/10 transition-colors cursor-pointer text-sm font-medium"
                  >
                    Edit Profile
                  </button>
                ) : null}

                {extraProfileLinks.map((link) => {
                  if (typeof link.show === "function" && !link.show()) return null;
                  return (
                    <button
                      key={link.path || link.label}
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        navigate(link.path);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-white/10 transition-colors cursor-pointer text-sm font-medium"
                    >
                      {link.label}
                    </button>
                  );
                })}

                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 hover:bg-white/10 transition-colors cursor-pointer text-sm font-medium"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  };

  return Header;
}
